import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";
import { deleteToken } from "@modules/token";
import { cancelTranscodeAllSync } from "@modules/upload";
import * as jobStatus from "@cores/jobStatus";
import { sessionKeeper } from "@cores/sessionKeeper";
import config from "@config/config";
import { JOB_ID_LCOAL_STORAGE_KEY } from "@constants";

import theme from "@styles/theme";
import { Tooltip, IconButton, Grid, Typography, Popover, Box, Divider, Stack } from "@mzc-pdc/ui";
import { IconOpenInNew } from "@mzc-cloudplex/icons";

import { Email, Name, ProfileItem, ToggleDivider, ToggleItem, ToggleList, RoundedButton } from "./profile-menu.styled";
import { UserProfile } from "@features/user-profile/user-profile";
import DialogCancelWarning from "@features/dialog/dialog-cancel-warning";
import { isLocal } from "@cores/enviromentExtension";
import LanguageMenu from "@features/language-menu/language-menu";
import useLanguageMenu from "@hooks/useLanguageMenu";
import useTermsLinks from "@hooks/useTermsLinks";
import { OverflowTooltip } from "@components_v2/ellipsis-tooltip";

export const ProfileMenu = ({ headerTheme }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const languageMenu = useLanguageMenu();

    const [modal, setModal] = useState("");
    const [warningMessage, setWarningMessage] = useState("");
    const [runningJobs, setRunningJobs] = useState([]);

    const user = useSelector(({ user }) => user);
    const upload = useSelector(({ upload }) => upload);

    const [profileModalActive, setProfileModalActive] = useState(false);

    const anchorRef = useRef(null);

    const { openTermsPrivacy, openTermsService } = useTermsLinks(languageMenu.locale);

    const openCustomerService = useCallback(() => {
        window.open(`https://help.cloudplex.megazone.io/hc/ko`);
    }, []);

    const handleToggle = useCallback(() => {
        setProfileModalActive((prevOpen) => !prevOpen);
    }, []);

    const handleClose = useCallback((event) => {
        setProfileModalActive(false);
    }, []);

    const onHandleClickProfile = useCallback(() => {
        window.open(`https://myaccount.megazone.com/info`);
    }, []);
    const isPopsUser = user?.data?.issuer === "megazone.com";
    const logOutPops = (value) => {
        setModal("");
        sessionKeeper.reset();
        if (value) {
            const welcomePath = window.location.pathname.indexOf("/library") > -1 ? "/library/welcome" : "/welcome";
            const redirectUrl = `${window.location.origin}${welcomePath}`;
            window.location.href = `${config.mz_pops.oauth_host}/logout?next=${redirectUrl}`;
        }
        dispatch(deleteToken());
    };

    useEffect(() => {
        const jobs = (upload.jobs.data ?? []).filter(
            (v) => v.status === jobStatus.values.READY || v.status === jobStatus.values.INGESTING,
        );
        setRunningJobs(jobs);
    }, [upload.jobs.data]);

    const proceedLogout = useCallback(() => {
        const domain = sessionKeeper.getDomain();
        Cookies.remove("beforeLoginUrl", { domain });
        localStorage.removeItem(JOB_ID_LCOAL_STORAGE_KEY);
        if (isPopsUser) {
            setModal("logout");
            setWarningMessage(t(`common::msg::${"Would you like to log out from the account service as well?"}`));
        } else {
            sessionKeeper.reset();
            dispatch(deleteToken());
        }
    }, [isPopsUser]);

    const onHandleClickLogout = useCallback(() => {
        if (runningJobs?.length > 0) {
            setModal("cancelJobs");
            setWarningMessage(t(`common::msg::${"There is work in progress. Would you really go out?"}`));
            return;
        }
        proceedLogout();
    }, [runningJobs, proceedLogout]);

    return (
        <Grid>
            <Tooltip title={t(`common::label::Profile`, `Profile`)}>
                <IconButton size={"large"} circled ref={anchorRef} onClick={handleToggle}>
                    <UserProfile profileImageUrl={user?.data?.profileImageUrl} headerTheme={headerTheme} size={24} />
                </IconButton>
            </Tooltip>
            <Popover
                open={profileModalActive}
                anchorEl={anchorRef.current}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <ToggleList>
                    <ProfileItem>
                        <Typography variant={"caption"} color={theme.palette.greySecondary[600]}>
                            <strong>{t(`common::label::Account`, `Account`)}</strong>
                        </Typography>
                        <Grid container alignItems={`center`} sx={{ marginTop: `12px` }} flexWrap={"nowrap"}>
                            <Grid item sx={{ marginRight: `12px` }}>
                                <UserProfile profileImageUrl={user?.data?.profileImageUrl} size={44} />
                            </Grid>
                            {isPopsUser ? (
                                <Grid item sx={{ minWidth: 0 }}>
                                    {/* 이름·이메일은 말줄임 처리되므로 잘린 경우에만 툴팁으로 전체 값을 노출한다. */}
                                    <OverflowTooltip>
                                        <Name>{user?.data?.displayName}</Name>
                                    </OverflowTooltip>
                                    <OverflowTooltip>
                                        <Email>{user?.data?.username}</Email>
                                    </OverflowTooltip>
                                </Grid>
                            ) : (
                                <Grid item>
                                    <Name sx={{ whiteSpace: `normal` }}>{user?.data?.id}</Name>
                                </Grid>
                            )}
                        </Grid>
                    </ProfileItem>
                    {isPopsUser && (
                        <>
                            <ToggleDivider />
                            <ToggleItem onClick={onHandleClickProfile}>
                                <strong>{t(`common::label::Manage Account`, `Manage Account`)}</strong>
                                <IconOpenInNew size={16} />
                            </ToggleItem>
                        </>
                    )}
                    <ToggleDivider />
                    <ToggleItem onClick={openCustomerService}>
                        <strong>{t(`common::label::Customer Service`)}</strong>
                        <IconOpenInNew size={16} />
                    </ToggleItem>
                    <ToggleDivider />
                    <ToggleItem onClick={onHandleClickLogout}>
                        {/*onClick={handleLogout}*/}
                        <strong> {t(`common::label::Log out`, `Log out`)}</strong>
                    </ToggleItem>
                    <ToggleDivider />
                    <Grid
                        container
                        justifyContent={"space-between"}
                        py={theme.spacing(0.75)}
                        px={theme.spacing(1)}
                        gap={theme.spacing(1)}
                    >
                        <Stack direction={"row"} gap={theme.spacing(1.5)}>
                            <RoundedButton
                                size={"xsmall"}
                                variant={"contained"}
                                color={"primary"}
                                onClick={openTermsPrivacy}
                            >
                                {t(`common::label::Privacy policy`)}
                            </RoundedButton>
                            <Divider orientation="vertical" flexItem />
                            <RoundedButton
                                size={"xsmall"}
                                variant={"contained"}
                                color={"greySecondary"}
                                onClick={openTermsService}
                            >
                                {t(`common::label::Terms & Conditions`)}
                            </RoundedButton>
                        </Stack>
                        <Box>
                            <LanguageMenu
                                locales={languageMenu.locales}
                                locale={languageMenu.locale}
                                label={languageMenu.label}
                                onClick={languageMenu.onChange}
                            />
                        </Box>
                    </Grid>
                </ToggleList>
            </Popover>
            {modal === "cancelJobs" && (
                <DialogCancelWarning
                    onClose={(value) => {
                        setModal("");
                        if (value) {
                            dispatch(cancelTranscodeAllSync(runningJobs));
                            proceedLogout();
                        }
                    }}
                    message={warningMessage}
                />
            )}
            {modal === "logout" && (
                <DialogCancelWarning onClose={(value) => logOutPops(value)} message={warningMessage} />
            )}
        </Grid>
    );
};
export default ProfileMenu;
